import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

const DEFAULT_PAGE_SIZE = 10;

function sanitizeNumber(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function sanitizeSortDirection(value) {
  return value === 'desc' ? 'desc' : 'asc';
}

export function useEmpleadosFilters() {
  const [params, setParams] = useSearchParams();

  const state = useMemo(() => {
    const page = sanitizeNumber(params.get('page'), 1);
    const pageSize = sanitizeNumber(params.get('pageSize'), DEFAULT_PAGE_SIZE);
    const search = params.get('search') || '';
    const estado = params.get('estado') || '';
    const empresaId = params.get('empresaId') || '';
    const sucursalId = params.get('sucursalId') || '';
    const fechaIngresoFrom = params.get('fechaIngresoFrom') || '';
    const fechaIngresoTo = params.get('fechaIngresoTo') || '';
    const sortField = params.get('sortField') || 'apellido';
    const sortDirection = sanitizeSortDirection(params.get('sortDirection'));

    return {
      page,
      pageSize,
      filters: {
        search,
        estado,
        empresaId,
        sucursalId,
        fechaIngresoFrom,
        fechaIngresoTo,
      },
      sort: {
        field: sortField,
        direction: sortDirection,
      },
    };
  }, [params]);

  const updateParams = useCallback(
    updater => {
      setParams(prev => {
        const next = new URLSearchParams(prev);
        updater(next);
        return next;
      });
    },
    [setParams],
  );

  const setFilters = useCallback(
    newFilters => {
      updateParams(next => {
        Object.entries(newFilters).forEach(([key, value]) => {
          if (value) {
            next.set(key, value);
          } else {
            next.delete(key);
          }
        });
        next.set('page', '1');
      });
    },
    [updateParams],
  );

  const setPage = useCallback(
    page => {
      updateParams(next => {
        next.set('page', String(page));
      });
    },
    [updateParams],
  );

  const setPageSize = useCallback(
    pageSize => {
      updateParams(next => {
        next.set('pageSize', String(pageSize));
        next.set('page', '1');
      });
    },
    [updateParams],
  );

  const setSort = useCallback(
    sort => {
      updateParams(next => {
        if (sort?.field) {
          next.set('sortField', sort.field);
          next.set('sortDirection', sanitizeSortDirection(sort.direction));
        } else {
          next.delete('sortField');
          next.delete('sortDirection');
        }
      });
    },
    [updateParams],
  );

  const resetFilters = useCallback(() => {
    setParams(new URLSearchParams());
  }, [setParams]);

  return {
    page: state.page,
    pageSize: state.pageSize,
    filters: state.filters,
    sort: state.sort,
    setFilters,
    setPage,
    setPageSize,
    setSort,
    resetFilters,
  };
}

export default useEmpleadosFilters;
